/**
 * Likeness trend — a compact multi-series line chart, hand-rolled as inline SVG.
 *
 * Deliberately NOT a charting library: quill-admin ships no charting dependency
 * and M3.4 is not a good enough reason to add ~500 kB of recharts + its D3
 * transitive tree to an internal admin bundle for one chart. The whole thing is
 * ~120 lines of SVG maths with no runtime cost.
 *
 * Four series, because the backend splits the DB's 3-value rater_type enum into
 * four real rater populations (member / family / Mirror critic / nightly
 * harness). Keeping them apart is the point of the chart: "family says it sounds
 * like him, the harness disagrees" is the interesting signal, and an averaged
 * single line would hide it.
 *
 * y is pinned to the full 0..1 score range (never auto-scaled) so two members'
 * charts are directly comparable and a flat-but-high series does not look like a
 * flat-but-low one.
 *
 * Ported from quill-app/quill-admin's LikenessTrendChart.tsx. The four series
 * colors and axis/gridline colors previously came from quillColors; they're now
 * derived from the lifescribe theme via useTheme() (deepTeal → primary,
 * burgundy → error — the two palettes share these hues exactly — plus
 * secondary.dark for the family series and text.secondary for the nightly
 * harness series, matching the source's warmGoldHc / mutedText intent).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import { LikenessSeriesKey, LikenessTrendPoint, LikenessTrendSeries } from '../../../services/quillAdminService';

function seriesMeta(theme: Theme): { key: LikenessSeriesKey; label: string; color: string; hint: string }[] {
  return [
    { key: 'member', label: 'Member', color: theme.palette.primary.main, hint: 'The member rating their own Mirror' },
    {
      key: 'family',
      label: 'Family',
      color: theme.palette.secondary.dark,
      hint: '"Did that sound like them?" from the family Mirror surface',
    },
    {
      key: 'mirror_critic',
      label: 'Mirror critic',
      color: theme.palette.error.main,
      hint: 'Automated critic score on each Mirror reply',
    },
    {
      key: 'nightly_harness',
      label: 'Nightly harness',
      color: theme.palette.text.secondary,
      hint: 'Nightly stylometry harness, persona vs. corpus',
    },
  ];
}

const W = 720;
const H = 240;
const PAD = { top: 12, right: 14, bottom: 30, left: 36 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;
const Y_TICKS = [0, 0.25, 0.5, 0.75, 1];

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const LikenessTrendChart: React.FC<{
  series: LikenessTrendSeries | null | undefined;
  bucket?: string;
}> = ({ series, bucket }) => {
  const theme = useTheme();
  const SERIES_META = seriesMeta(theme);

  const safe = (key: LikenessSeriesKey): LikenessTrendPoint[] => {
    const s = series ? series[key] : undefined;
    return Array.isArray(s) ? s : [];
  };

  const allPoints = SERIES_META.flatMap((m) => safe(m.key));
  if (allPoints.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No likeness feedback recorded yet — nothing to trend.
      </Typography>
    );
  }

  const times = allPoints.map((p) => new Date(p.date).getTime()).filter((t) => !Number.isNaN(t));
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const span = maxT - minT;

  // A single bucket (or all points on one day) draws down the middle rather than
  // collapsing onto the left edge.
  const x = (iso: string): number => {
    const t = new Date(iso).getTime();
    if (!span || Number.isNaN(t)) return PAD.left + PLOT_W / 2;
    return PAD.left + ((t - minT) / span) * PLOT_W;
  };
  const y = (v: number): number => PAD.top + (1 - Math.max(0, Math.min(1, v))) * PLOT_H;

  return (
    <Box>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Likeness trend by rater population"
          style={{ width: '100%', height: 'auto', display: 'block', minWidth: 320 }}
        >
          <title>Likeness score over time, split by rater population</title>

          {/* y gridlines + labels */}
          {Y_TICKS.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={PAD.left + PLOT_W}
                y1={y(t)}
                y2={y(t)}
                stroke={theme.palette.divider}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 6}
                y={y(t) + 3.5}
                textAnchor="end"
                fontSize={10}
                fill={theme.palette.text.secondary}
              >
                {t.toFixed(2)}
              </text>
            </g>
          ))}

          {/* x axis end labels */}
          <text x={PAD.left} y={H - 10} fontSize={10} fill={theme.palette.text.secondary}>
            {shortDate(new Date(minT).toISOString())}
          </text>
          {span > 0 && (
            <text
              x={PAD.left + PLOT_W}
              y={H - 10}
              fontSize={10}
              textAnchor="end"
              fill={theme.palette.text.secondary}
            >
              {shortDate(new Date(maxT).toISOString())}
            </text>
          )}

          {/* one polyline + point markers per rater series */}
          {SERIES_META.map((meta) => {
            const points = safe(meta.key);
            if (points.length === 0) return null;
            const coords = points.map((p) => `${x(p.date).toFixed(1)},${y(p.avg).toFixed(1)}`);
            return (
              <g key={meta.key} data-testid={`series-${meta.key}`}>
                {points.length > 1 && (
                  <polyline
                    points={coords.join(' ')}
                    fill="none"
                    stroke={meta.color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}
                {points.map((p) => (
                  <circle
                    key={`${meta.key}-${p.date}`}
                    cx={x(p.date)}
                    cy={y(p.avg)}
                    r={3}
                    fill={meta.color}
                  >
                    <title>{`${meta.label} · ${shortDate(p.date)} · ${p.avg.toFixed(2)} (n=${p.n})`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </svg>
      </Box>

      {/* Legend — n is the rater count behind each line, which is how much the */}
      {/* line should be trusted. A one-rating line is noise, not a trend.      */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
        {SERIES_META.map((meta) => {
          const points = safe(meta.key);
          const total = points.reduce((acc, p) => acc + p.n, 0);
          return (
            <Box
              key={meta.key}
              title={meta.hint}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: total ? 1 : 0.4 }}
            >
              <Box
                sx={{ width: 14, height: 3, borderRadius: 2, bgcolor: meta.color, flexShrink: 0 }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {meta.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                n={total}
              </Typography>
            </Box>
          );
        })}
        {bucket && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            bucketed by {bucket}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LikenessTrendChart;
